// Per-board snapshot cache. Persists the latest applied `MatrixState`
// and `seq` so a cold load paints the matrix immediately, before the
// WebSocket handshake completes. The server is always authoritative —
// `applyServerMsg` overwrites whatever we seeded as soon as the first
// snapshot or replay lands. The cached `seq` also lets the server
// reply with a delta `Replay` instead of a full `Snapshot` when the
// cache is recent enough (see `worker/src/eyes_room.rs`).

import type { MatrixState } from "../generated/MatrixState";

// Bump when the on-disk shape becomes incompatible with older bundles
// (e.g. when MatrixState.v changes). Old entries silently fail to load.
const STORAGE_VERSION = 1;

interface CacheEnvelope {
    v: number;
    seq: number;
    state: MatrixState;
}

function cacheKey(boardId: string): string {
    return `eyes_board_v${STORAGE_VERSION}_${boardId}`;
}

export interface CachedBoard {
    state: MatrixState;
    seq: number;
}

/** Read the cached snapshot for `boardId`. Returns null on any failure
 *  (missing entry, JSON garbage, schema mismatch, blocked storage). */
export function readBoardCache(boardId: string): CachedBoard | null {
    let raw: string | null;
    try {
        raw = localStorage.getItem(cacheKey(boardId));
    } catch {
        return null;
    }
    if (raw === null) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!isEnvelope(parsed)) return null;
    if (parsed.v !== STORAGE_VERSION) return null;
    if (parsed.state.v !== 1) return null;
    return { state: parsed.state, seq: parsed.seq };
}

/** Persist the current board state. Best-effort: a failed write (quota,
 *  private mode, sandbox) is swallowed so the app keeps running. */
export function writeBoardCache(
    boardId: string,
    state: MatrixState,
    seq: number,
): void {
    const env: CacheEnvelope = { v: STORAGE_VERSION, seq, state };
    try {
        localStorage.setItem(cacheKey(boardId), JSON.stringify(env));
    } catch {
        // Quota exceeded / storage disabled — ignore.
    }
}

export function clearBoardCache(boardId: string): void {
    try {
        localStorage.removeItem(cacheKey(boardId));
    } catch {
        // Ignore.
    }
}

function isEnvelope(x: unknown): x is CacheEnvelope {
    if (typeof x !== "object" || x === null) return false;
    const o = x as Record<string, unknown>;
    return (
        typeof o.v === "number" &&
        typeof o.seq === "number" &&
        typeof o.state === "object" &&
        o.state !== null
    );
}
